/**
 * The two public surfaces that are not a credential page: `#/verify` (`m09-public.js:35`) and
 * `#/claim/:token` (`m06-claim.js:53`).
 *
 * Verify is deliberately the smallest screen in the product — a code field and a button. There is
 * no search by name, e-mail or company anywhere on it, and that absence is the feature: a
 * verification surface that answers «who holds a credential» would turn a proof page into a
 * directory of people.
 *
 * Claim is the one screen a recipient sees, and it shows seven different answers at one address.
 * Which one is decided by `claimGate`, which runs the same tests in the same order as the domain
 * command behind the buttons — so the page a recipient lands on and the answer their click gets
 * can never disagree. Nothing on the claimable card is published until the button is pressed:
 * until then the record's public page carries no name, no claims and no artwork (rule 12).
 */
import { type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useV07Commands } from '../data/commands-context';
import { useDemoData } from '../data/store';
import type { DemoCredential } from '../data/types';
import {
  CLAIM_I18N,
  type ClaimLanguage,
  claimGate,
  claimRejectText,
  claimValidityDays,
  evaluateAccept,
  findByRawClaimToken,
} from '../lib/claim-flow';
import { celebrate } from '../lib/confetti';
import { useGoToCredential } from '../lib/go-to-credential';
import { usePageViewed } from '../lib/interaction';
import { credVisualClass, PUBLIC_I18N } from '../lib/public-page';
import { credTitle, displayName, fmtDate } from '../lib/reference';
import { credentialArtwork, credTerms, sealedInLanguage, servedArtifact } from '../lib/render';
import { CLAIM_FOOTER, PublicShell } from '../shell/PublicShell';
import { Modal, ModalHead } from '../ui/Modal';
import { PublicLink } from '../ui/PublicLink';
import { useToast } from '../ui/toast';

// ------------------------------------------------------------------------------ shared plumbing

const LANG_KEY = 'hpw_lang';

function storedLang(): ClaimLanguage {
  try {
    return localStorage.getItem(LANG_KEY) === 'en' ? 'en' : 'tr';
  } catch {
    /* A blocked or cleared storage must not take the language switch down with it. */
    return 'tr';
  }
}

/** The reference's `setLang`: the choice is remembered for the next public page. */
function useLang(): [ClaimLanguage, (next: ClaimLanguage) => void] {
  const [lang, setLang] = useState<ClaimLanguage>(storedLang);
  return [
    lang,
    (next) => {
      setLang(next);
      try {
        localStorage.setItem(LANG_KEY, next);
      } catch {
        /* see above */
      }
    },
  ];
}

const subjectType = (c: DemoCredential): string =>
  String((c.subjectSnapshot as { subjectType?: unknown }).subjectType ?? '');

const tokenExpiry = (c: DemoCredential): string | null => {
  const v = c.claimToken?.expiresAt;
  return typeof v === 'string' ? v : null;
};

// ------------------------------------------------------------------------------------- #/verify

/** The lookup form both public surfaces carry; the verify page's copy has no initial value. */
function LookupForm({ placeholder, submit }: { placeholder: string; submit: string }): ReactElement {
  const goToCredential = useGoToCredential();
  /* A ref rather than a `name` or a controlled value: the reference's input carries neither, and
     an attribute this surface adds is an attribute the reference dump does not have. */
  const field = useRef<HTMLInputElement>(null);
  return (
    <form
      className="form"
      id="lookupForm"
      onSubmit={(event) => {
        event.preventDefault();
        const v = field.current?.value.trim() ?? '';
        if (v) goToCredential(v);
      }}
    >
      <input
        className="input"
        id="lookupCode"
        placeholder={placeholder}
        autoComplete="off"
        aria-label="Credential ID"
        ref={field}
      />
      <button className="btn btn-primary" type="submit">
        {submit}
      </button>
    </form>
  );
}

/** `#/verify` — the public shell, one field, and no record to show. */
export function VerifyScreen(): ReactElement {
  const [lang, setLang] = useLang();
  const L = PUBLIC_I18N[lang];
  return (
    <PublicShell id="publicScreen" brandSub={L.brandSub} lang={lang} onLang={setLang} footer={L.foot}>
      <div className="notfound" style={{ marginTop: 60 }}>
        <span className="eyebrow">Happy Place to Work</span>
        <h1>{L.verifyTitle}</h1>
        <p className="muted">{L.verifyIntro}</p>
        <LookupForm placeholder={L.lookupPh} submit={L.lookup} />
      </div>
    </PublicShell>
  );
}

// -------------------------------------------------------------------------------- #/claim/:token

/** `claimNotice` — `m06-claim.js:49`. The tick, ring and cross are the reference's own glyphs. */
function ClaimNotice({
  kind,
  heading,
  body,
  children,
}: {
  kind: 'active' | 'expired' | 'revoked';
  heading: string;
  body: string;
  children?: ReactNode;
}): ReactElement {
  return (
    <div className="notfound">
      <div className={`status ${kind}`}>
        <span className="ico">{kind === 'active' ? '✓' : kind === 'expired' ? '○' : '✕'}</span>
        {heading}
      </div>
      <p className="muted">{body}</p>
      {children}
    </div>
  );
}

interface ClaimCardProps {
  credential: DemoCredential;
  /** The raw token from the address, which is what every command is keyed on. */
  token: string;
  lang: ClaimLanguage;
  /** Set when the SERVER resolved the token; the reference's own lookup is then not attempted. */
  resolved?: DemoCredential | null | undefined;
}

/**
 * The claimable card: what the recipient reads before deciding, and the four things they may do.
 *
 * «Şimdilik yayınlama» changes nothing on the record — it answers with the info box and leaves the
 * link usable — because the frozen reference has no decline command, and a screen may not invent
 * a lifecycle transition the domain does not define.
 */
function ClaimCard({ credential: c, token, lang, resolved }: ClaimCardProps): ReactElement {
  const db = useDemoData();
  const commands = useV07Commands();
  const goToCredential = useGoToCredential();
  const toast = useToast();
  const L = CLAIM_I18N[lang];
  const T = credTerms(c, lang);
  const isOrg = subjectType(c) === 'organization';

  const [note, setNote] = useState<ReactElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [reporting, setReporting] = useState(false);
  const message = useRef<HTMLTextAreaElement>(null);

  /*
   * THE STORED DOCUMENT FOR THIS LANGUAGE, OR NO DOCUMENT AT ALL.
   *
   * `credentialArtwork` draws a certificate in the browser when it finds no stored one, which is right
   * where the drawing IS the product — the editor's canvas — and wrong here: the recipient is being
   * asked to accept a sealed document, and the one thing this screen may not do is show them something
   * that was never sealed. Worse, before this the claim page fetched ONE document and handed it to both
   * tabs, so a record sealed in two languages showed its Turkish certificate as the English one.
   *
   * `artifactSvgByLocale` is the product surface's per-language documents, and it now carries one
   * entry per sealed language even when the record seals a single one — so a Turkish-only record
   * answers with its document for Turkish and with NOTHING for English, and the notice below stands
   * in the document's place: never the other language's picture, and never a drawing this page made
   * up. The demo surface is handed no documents at all and keeps drawing from the design, which is
   * what the visual acceptance measures.
   *
   * The test is the public page's own, word for word: a language the release does not seal, or one
   * that seals it but whose document did not arrive, is a language with no document here.
   */
  const servedByLocale = (c as { artifactSvgByLocale?: Partial<Record<'tr' | 'en', string | null>> })
    .artifactSvgByLocale;
  const documentMissing =
    servedByLocale !== undefined && (!sealedInLanguage(c, lang) || servedArtifact(c, lang) === null);
  const svg = useMemo(
    () => (documentMissing ? '' : credentialArtwork(c, { lang })),
    [c, lang, documentMissing],
  );

  /*
   * «Kabul sayfası açıldı» — usage, not a legal act, so it stays out of the append-only ledger
   * (§7) and goes to the durable usage measurement instead (PO 7.12 §3).
   *
   * It used to go to a stream that lived in the tab and was gone on refresh, which meant a record's
   * history could never say the recipient had opened their link. The ref keeps a re-render (and
   * StrictMode's double effect) from recording it twice here; the key does the same across a
   * retried request. It records that the page was opened and nothing about who opened it.
   */
  usePageViewed(c.publicCode, 'claim_opened');

  /*
   * A short burst of confetti, the first time a recipient opens a certificate that is waiting for
   * them — and only then.
   *
   * This component renders exactly one of the claim gate's seven answers: the CLAIMABLE one. An
   * expired, used, revoked, declined or unknown link is a different branch that never reaches here,
   * so «no celebration on a bad link» holds by where this call is rather than by a condition that
   * could be got wrong.
   *
   * `celebrate` itself refuses to repeat for the same record in this tab and refuses outright under
   * `prefers-reduced-motion`; the cleanup stops it if the recipient navigates away mid-burst. The
   * effect keys on the record, so a re-render, a language switch or a dialog opening cannot restart
   * it. The token is not part of the key and is never passed in.
   */
  useEffect(() => celebrate(c.id), [c.id]);

  /**
   * Accepting: one decision, one outcome.
   *
   * There is no longer a second button that accepts AND opens LinkedIn. A recipient deciding
   * whether their name may be published should not be deciding about LinkedIn in the same press —
   * and the tab that press opened landed on a profile form before the record it was for had
   * finished publishing. The profile helper is unchanged and is where it belongs: on the public
   * page, after the record exists.
   */
  const runAccept = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    /*
     * The reference's own read of the link — expired, already used, revoked — reported without a
     * round trip. It is a courtesy, not the decision: the command below is the only thing that can
     * consume a token, and it is consumed server-side.
     */
    const res = evaluateAccept(db, token, undefined, resolved);
    if (!res.ok && !res.idempotent) {
      setBusy(false);
      setNote(
        <div className="warn-box">{res.message ?? claimRejectText(res.reason ?? 'not_found', lang)}</div>,
      );
      return;
    }
    const done = await commands.acceptClaim({ token });
    if (!done.ok) {
      setBusy(false);
      setNote(<div className="warn-box">{done.message ?? claimRejectText('not_found', lang)}</div>);
      return;
    }
    const code = done.publicCode ?? c.publicCode;
    toast(L.published);
    goToCredential(code, '?published=1');
  };

  const sendReport = async (): Promise<void> => {
    const text = message.current?.value.trim() || '—';
    const r = await commands.reportClaimIssue({ credentialId: c.id, message: text });
    setReporting(false);
    setNote(
      r.ok ? (
        <div className="ok-box">{L.reportOk}</div>
      ) : (
        <div className="warn-box">{r.message ?? L.reportOk}</div>
      ),
    );
  };

  return (
    <article className="cred-card claim-card">
      {/* The artwork is the record's own render, produced from its issuance release — never from
          the template as it stands today (binding rules 3 and 4). */}
      {documentMissing ? (
        <div className="cred-visual" id="claimNoLocaleDocument">
          <p className="status-note" style={{ padding: '18px', margin: 0, textAlign: 'center' }}>
            {L.noDocumentInLanguage}
          </p>
        </div>
      ) : (
        <div className={`cred-visual ${credVisualClass(c)}`} dangerouslySetInnerHTML={{ __html: svg }} />
      )}
      <section className="cred-info">
        <div>
          <span className="eyebrow yellow">{L.eyebrow}</span>
          <h1 style={{ marginTop: 8 }}>{L.title(T)}</h1>
          <p className="muted" style={{ marginTop: 8 }}>
            {L.intro}
          </p>
        </div>
        <dl className="cred-dl">
          <div className="full">
            <dt>{L.dl.title}</dt>
            <dd>{credTitle(c, lang)}</dd>
          </div>
          <div className="full">
            <dt>{isOrg ? L.dl.holderOrg : L.dl.holder}</dt>
            <dd>
              <b style={{ fontSize: 18 }}>{displayName(c)}</b>
            </dd>
          </div>
          <div>
            <dt>{L.dl.issuer}</dt>
            <dd>{c.release.issuerProfileSnapshot.displayName}</dd>
          </div>
          <div>
            <dt>{L.dl.issue}</dt>
            <dd>{fmtDate(c.issueDate, lang)}</dd>
          </div>
          <div>
            <dt>{L.dl.expiry}</dt>
            <dd>{c.expiresAt ? fmtDate(c.expiresAt, lang) : L.dl.noExpiry}</dd>
          </div>
          <div>
            <dt>{L.dl.id}</dt>
            <dd className="code">
              <span>{c.publicCode}</span>
            </dd>
          </div>
        </dl>
        <p className="help" id="claimConsent" style={{ margin: 0 }}>
          {L.consent(isOrg)}
        </p>
        <div className="claim-actions">
          <button
            className="btn btn-primary btn-lg"
            type="button"
            id="claimAccept"
            aria-describedby="claimConsent"
            disabled={busy}
            onClick={() => void runAccept()}
          >
            {L.accept}
          </button>
          <button
            className="btn btn-secondary"
            type="button"
            id="claimLater"
            onClick={() => setNote(<div className="info-box">{L.laterBox(claimValidityDays(db))}</div>)}
          >
            {L.later}
          </button>
          <button className="btn btn-ghost" type="button" id="claimReport" onClick={() => setReporting(true)}>
            {L.report}
          </button>
        </div>
        <div id="claimNote" role="status" aria-live="polite">
          {note}
        </div>
        {/*
         * The validity of this link, and nothing about LinkedIn.
         *
         * The sentence used to continue «LinkedIn'e ekleme, onaydan sonra profil formunu açar…»,
         * which described the combined button that is no longer here. The profile helper still
         * exists — it is on the public page, after the record is published — so saying it here
         * would be explaining a control this screen does not offer.
         */}
        <p className="help">{L.validity(fmtDate(tokenExpiry(c), lang))}</p>
      </section>
      {reporting ? (
        <Modal label={L.reportTitle} onClose={() => setReporting(false)}>
          <ModalHead title={L.reportTitle} onClose={() => setReporting(false)} />
          <div className="card-pad">
            <div className="field">
              <label htmlFor="rpMsg">{L.reportLabel}</label>
              <textarea className="textarea" id="rpMsg" placeholder={L.reportPh} ref={message} />
            </div>
          </div>
          <div className="card-foot">
            <button
              className="btn btn-ghost"
              data-close="1"
              type="button"
              onClick={() => setReporting(false)}
            >
              {L.cancel}
            </button>
            <button className="btn btn-primary" id="rpSend" type="button" onClick={() => void sendReport()}>
              {L.reportSend}
            </button>
          </div>
        </Modal>
      ) : null}
    </article>
  );
}

export interface ClaimScreenProps {
  /**
   * The record this link resolves to, when the caller already knows it.
   *
   * The demo does not pass it: its store holds the reference's whole database and the outbox that
   * maps a raw token to a record, so the lookup below is the reference's own and the parity
   * capture measures exactly that path.
   *
   * The product does pass it. There the server resolved the token — hashing it, in constant time,
   * with no raw token at rest — and the page holds ONE record and no outbox. Re-deriving the
   * answer here would need the raw token in client state, which is the one thing the claim surface
   * must not keep; and `claimGate` still decides which of the seven answers is rendered, from the
   * state and token facts the preview's verdict was mapped onto.
   */
  credential?: DemoCredential | null;
}

/** `#/claim/:token` — the claim shell and whichever of the seven answers this link earns. */
export function ClaimScreen({ credential }: ClaimScreenProps = {}): ReactElement {
  const db = useDemoData();
  const params = useParams();
  const { pathname } = useLocation();
  const [lang, setLang] = useLang();
  const L = CLAIM_I18N[lang];

  /* The demo router owns the route table; reading the path as well means a `/claim/*` mounting and
     a `/claim/:token` one resolve the same link instead of answering «bulunamadı». */
  const fromPath = /\/claim\/([^/?#]+)/.exec(pathname);
  const token = params.token ?? (fromPath?.[1] ? decodeURIComponent(fromPath[1]) : '');

  const gate = claimGate(credential === undefined ? findByRawClaimToken(db, token) : credential);

  let body: ReactElement;
  if (gate.kind === 'not_found') {
    body = (
      <div className="notfound">
        <div className="big">404</div>
        <h1>{L.notFound}</h1>
        <p className="muted">{L.notFoundNote}</p>
      </div>
    );
  } else if (gate.kind === 'credential_revoked') {
    body = (
      <ClaimNotice
        kind="revoked"
        heading={credTerms(gate.credential, lang).revoked}
        body={claimRejectText('credential_revoked', lang)}
      />
    );
  } else if (gate.kind === 'used') {
    /*
     * A consumed link is a success, not an error: the record is live, so the recipient is offered
     * the page rather than told off for opening their e-mail twice.
     *
     * AND THE PAGE IS FETCHED, NOT REPAINTED. This was a `<Link>`, and a client-side transition
     * leaves the document the server sent for `/claim/:token` in place while the address bar says
     * `/c/…` — including its `Referrer-Policy: no-referrer`, where `/c/:code` is the one response
     * that carries `strict-origin-when-cross-origin`. Under `no-referrer` Chromium puts `Origin:
     * null` on every cross-site form POST, so the LinkedIn share's start — a form POST into the
     * authorization window — presented no usable origin and the API refused it before it looked at
     * the ticket. A recipient who accepted, shared once, then reopened the same e-mail link and
     * came back through THIS button could not share again; the same person on the same address in a
     * fresh load could, which is why reloading or clearing site data appeared to «fix» it.
     *
     * `go-to-credential.ts` says the rest, and names the three transitions already moved for this
     * reason; this was the fourth. A plain `<a>` is the declarative form of the same thing —
     * `useHref` keeps the demo's hash router in-page, and renders the identical `href` the
     * `<Link>` did, so nothing about this control's markup or its pixels changes.
     */
    body = (
      <ClaimNotice kind="active" heading={L.published} body={L.used}>
        <PublicLink className="btn btn-primary" to={`/c/${gate.credential.publicCode}`}>
          {L.usedBtn}
        </PublicLink>
      </ClaimNotice>
    );
  } else if (gate.kind === 'token_revoked') {
    body = <ClaimNotice kind="revoked" heading={L.notFound} body={claimRejectText('revoked_token', lang)} />;
  } else if (gate.kind === 'token_expired') {
    body = (
      <ClaimNotice
        kind="expired"
        heading={L.notFound}
        body={`${claimRejectText('expired', lang)} ${L.expiredNote}`}
      />
    );
  } else if (gate.kind === 'wrong_state') {
    body = <ClaimNotice kind="expired" heading={L.notFound} body={claimRejectText('wrong_state', lang)} />;
  } else {
    body = <ClaimCard credential={gate.credential} token={token} lang={lang} resolved={credential} />;
  }

  return (
    <PublicShell
      id="claimScreen"
      brandSub="Onay ve yayınlama"
      lang={lang}
      onLang={setLang}
      footer={CLAIM_FOOTER}
    >
      {body}
    </PublicShell>
  );
}
